import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { Ico } from './ui'
import ReproductorFormacion from './formacion/ReproductorFormacion'
import { leccionesDeLaPantalla, minutos } from '../utils/comoSeHace'
import { engancharCierre } from '../utils/nubeDesplegables'
import { t, marca, tDato } from '../utils/i18n'

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  «CÓMO SE HACE»: EL VÍDEO DE LA FORMACIÓN DE ESTA PANTALLA, ARRIBA Y A UN TOQUE              (t578 · 30-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño (30-oct): «cada pantalla, con una especie de botón arriba que no moleste pero se vea». UN componente, montado una
//  vez en la cabecera de la aplicación (components/Layout, junto al nombre de la pantalla) y otra en la del panel de
//  administración (components/AdminLayout). Ninguna pantalla lleva el suyo: qué vídeos tiene cada una es un DATO
//  (el mapa del servidor, que llega por GET /api/formacion/como-se-hace) y la pantalla de ahora la decide
//  utils/comoSeHace · leccionesDeLaPantalla con la dirección (también /pacientes?paciente=…&tab=…, con sus parámetros).
//
//  LO QUE HACE:
//   · una pantalla sin vídeo no pinta NADA (ni un hueco en la cabecera);
//   · con UN vídeo, el botón abre la ventana con él; con VARIOS, un desplegable con sus títulos y lo que dura cada uno;
//   · la ventana trae el reproductor de la Formación (components/formacion/ReproductorFormacion, el mismo de la Formación y de la
//     ayuda: enlace firmado, subtítulos, capítulos, y la lección queda vista al acabarla) y «Ver la lección completa en la
//     Formación» (salvo las que no están en el curso, la 17 y la 140); con varios, los demás vídeos de la pantalla debajo;
//   · quien no tiene la Formación (clínica de baja o en pausa, persona con la Formación cortada) recibe 403 y no ve nada; quien
//     todavía no ha aceptado sus condiciones ve el botón y, dentro, que primero las acepte.
//  DISCRETO: una pastilla con el color de la clínica (`--primary`, su tinta y su fondo suave); donde la cabecera va justa
//  (`compacto`), el icono solo; y donde ni así cabe (un móvil estrecho: el nivel 7 de la cabecera, components/Layout ·
//  siguienteNivelCabecera), al menú del avatar (ComoSeHaceEnMenu), como el «?» o el reloj. El desplegable lleva
//  [data-desplegable-cabecera] (la barra sube a su capa mientras está abierto) y se cierra al pulsar fuera, con Escape o al
//  cambiar de pantalla; la ventana va a <body>, en la capa de las ventanas.
//  Lo mide backend/routes/como-se-hace.test.js.
export const CLAVE_COMO_SE_HACE = ['formacion-como-se-hace']
export const TXT_BOTON = marca('components.ComoSeHace.boton', 'Cómo se hace')   // se pinta con tDato(TXT_BOTON)

// LA ENTRADA DEL MENÚ DEL AVATAR la abre así (cuando la cabecera de la aplicación no tiene sitio para el botón): avisa al
// «Cómo se hace» montado en la cabecera, que es el que tiene el desplegable y la ventana. Como el reloj (utils/reloj · abrirReloj).
const oyentes = new Set()
export function abrirComoSeHace() {
  for (const f of [...oyentes]) { try { f() } catch (_) { /* uno roto no para a los demás */ } }
}

// Lo que dice el servidor de los vídeos de cada pantalla (se pide una vez y se guarda: lo comparten el botón y la entrada del menú).
function useMapaComoSeHace() {
  return useQuery({
    queryKey: CLAVE_COMO_SE_HACE,
    queryFn: () => api.get('/formacion/como-se-hace').then(r => r.data),
    staleTime: 5 * 60 * 1000, retry: false,
  })
}

// `enMenu`: la cabecera de la aplicación no tiene sitio (components/Layout · siguienteNivelCabecera, nivel 7) y el botón está en
// el menú del avatar (ComoSeHaceEnMenu): aquí no se pinta nada en la barra, solo el desplegable y la ventana cuando se abren.
// `tam`: el diámetro del botón en compacto (32 px; en la cabecera del panel, 28, para que quepa en un portátil de 900 px).
export default function ComoSeHace({ ruta, busqueda = '', compacto = false, alinear = 'izquierda', enMenu = false, tam = 32 }) {
  const { data, refetch } = useMapaComoSeHace()
  const lecciones = leccionesDeLaPantalla(data, ruta, busqueda)
  const [lista, setLista] = useState(false)
  const [viendo, setViendo] = useState(null)
  const cajaRef = useRef(null)
  const varias = lecciones.length > 1
  const abrir = () => {
    if (!lecciones.length) return
    if (data && data.condiciones) refetch()   // por si las acaba de aceptar en la Formación
    if (!varias) { setViendo(lecciones[0].num); return }
    setLista(x => !x)
  }
  const abrirRef = useRef(abrir)
  abrirRef.current = abrir
  useEffect(() => {
    const f = () => abrirRef.current()
    oyentes.add(f)
    return () => { oyentes.delete(f) }
  }, [])
  // Al cambiar de pantalla se cierra lo que hubiera abierto: el vídeo era de la otra.
  useEffect(() => { setLista(false); setViendo(null) }, [ruta])
  useEffect(() => {
    if (!lista) return undefined
    return engancharCierre({ dentro: (el) => !!(cajaRef.current && cajaRef.current.contains && cajaRef.current.contains(el)), cerrar: () => setLista(false) })
  }, [lista])
  if (!lecciones.length) return null
  const elegida = viendo != null ? (lecciones.find(l => l.num === viendo) || null) : null
  const desplegable = lista && varias ? (
    <ListaComoSeHace lecciones={lecciones} compacto={compacto || enMenu} alinear={alinear}
      onElegir={(n) => { setLista(false); setViendo(n) }} />
  ) : null
  const ventana = elegida ? (
    <VentanaComoSeHace leccion={elegida} lecciones={lecciones} condiciones={!!(data && data.condiciones)}
      onElegir={setViendo} onCerrar={() => setViendo(null)} />
  ) : null
  if (enMenu) {
    // Sin nada en la barra (ni un hueco: un hijo vacío también se lleva su separación). El desplegable va a <body>, fijo bajo la
    // cabecera; la ventana ya va a <body> ella sola.
    if (!desplegable && !ventana) return null
    const fuera = <>{desplegable && <div ref={cajaRef} data-como-se-hace="menu">{desplegable}</div>}{ventana}</>
    return typeof document !== 'undefined' && document.body ? createPortal(fuera, document.body) : fuera
  }
  return (
    <div ref={cajaRef} data-como-se-hace="" style={{ position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
      <BotonComoSeHace compacto={compacto} varias={varias} abierta={lista} onClick={abrir} tam={tam} />
      {desplegable}
      {ventana}
    </div>
  )
}

// LA ENTRADA DEL MENÚ DEL AVATAR, cuando el botón no cabe en la barra (un móvil estrecho): «Cómo se hace», con cuántos vídeos
// tiene la pantalla. Solo si la pantalla tiene alguno. Abre lo mismo que el botón.
export function ComoSeHaceEnMenu({ ruta, busqueda = '', onAntes }) {
  const { data } = useMapaComoSeHace()
  const lecciones = leccionesDeLaPantalla(data, ruta, busqueda)
  if (!lecciones.length) return null
  return (
    <button type="button" data-menu-como-se-hace="" onClick={() => { if (onAntes) onAntes(); abrirComoSeHace() }}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', fontSize: '13px', color: 'var(--text-primary)',
        fontWeight: '500', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
      <Ico n="play" s={16} />
      <span style={{ flex: 1, minWidth: 0 }}>{tDato(TXT_BOTON)}</span>
      {lecciones.length > 1 && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{lecciones.length}</span>}
    </button>
  )
}

// EL BOTÓN: sin hooks (lo miden las guardias de la cabecera con su ancho de verdad). Una pastilla de 32 px de alto, con el play
// en un círculo del color de la clínica; en compacto, el círculo solo (de `tam` px).
export function BotonComoSeHace({ compacto = false, varias = false, abierta = false, onClick, tam = 32 }) {
  const rotulo = tDato(TXT_BOTON)
  const ayuda = varias
    ? t('components.ComoSeHace.ayuda_varios', 'Cómo se hace: los vídeos de esta pantalla')
    : t('components.ComoSeHace.ayuda_uno', 'Cómo se hace: el vídeo de esta pantalla')
  return (
    <button type="button" data-boton-como-se-hace="" onClick={onClick} title={ayuda} aria-label={ayuda}
      aria-haspopup={varias ? 'menu' : 'dialog'} aria-expanded={varias ? !!abierta : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', height: (compacto ? tam : 32) + 'px', boxSizing: 'border-box',
        width: compacto ? tam + 'px' : undefined, padding: compacto ? 0 : '0 11px 0 4px', borderRadius: '999px', flexShrink: 0,
        border: '1px solid color-mix(in srgb, var(--primary) 32%, var(--border))',
        background: abierta ? 'var(--primary-soft)' : 'color-mix(in srgb, var(--primary) 7%, var(--surface))',
        color: 'var(--primary-tinta)', fontSize: '12.5px', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', cursor: 'pointer',
      }}>
      <span aria-hidden="true" style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--primary)', color: 'var(--sobre-primary)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, paddingLeft: '2px', boxSizing: 'border-box' }}>
        <Ico n="play" s={11} fill="currentColor" sw={0} />
      </span>
      {!compacto && <span>{rotulo}</span>}
      {!compacto && varias && <Ico n="chevronD" s={13} />}
    </button>
  )
}

// EL DESPLEGABLE de una pantalla con varios vídeos: sus títulos, en el orden del curso, con lo que dura cada uno. En el
// ordenador cuelga del botón; en compacto (tablet y móvil) va fijo bajo la cabecera, de lado a lado con 12 px de margen.
export function ListaComoSeHace({ lecciones, compacto = false, alinear = 'izquierda', onElegir }) {
  const sitio = compacto
    ? { position: 'fixed', top: 'calc(64px + var(--sa-arriba))', left: '12px', right: '12px', maxWidth: '420px', margin: '0 auto' }
    : { position: 'absolute', top: 'calc(100% + 8px)', [alinear === 'derecha' ? 'right' : 'left']: 0, width: '340px', maxWidth: 'calc(100vw / var(--z, 1) - 24px)' }
  return (
    <div role="menu" data-desplegable-cabecera="" data-lista-como-se-hace=""
      style={{ ...sitio, zIndex: 'var(--capa-desplegable-abierto)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px',
        boxShadow: '0 12px 32px rgba(0,0,0,.16)', padding: '6px', boxSizing: 'border-box', maxHeight: 'calc(var(--alto-pantalla) - 96px)', overflowY: 'auto' }}>
      <div style={{ padding: '8px 10px 6px', fontSize: '11.5px', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
        {t('components.ComoSeHace.titulo_lista', 'Cómo se hace en esta pantalla')}
      </div>
      {lecciones.map(l => (
        <button key={l.num} type="button" role="menuitem" data-leccion-como-se-hace={l.num} onClick={() => onElegir(l.num)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '9px 10px', border: 'none', borderRadius: '8px', background: 'transparent',
            color: 'var(--text-primary)', fontSize: '13.5px', fontWeight: 600, textAlign: 'left', cursor: 'pointer', lineHeight: 1.3 }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)' }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
          <span aria-hidden="true" style={{ width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--primary-soft)', color: 'var(--primary-tinta)', paddingLeft: '2px', boxSizing: 'border-box' }}>
            <Ico n="play" s={11} fill="currentColor" sw={0} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>{l.titulo}</span>
          {minutos(l.duracion_seg) && <span style={{ flexShrink: 0, fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{minutos(l.duracion_seg)}</span>}
        </button>
      ))}
    </div>
  )
}

// LA VENTANA del vídeo: el reproductor de la Formación con los capítulos y el punto donde se dejó (GET /formacion/lecciones/:num,
// la misma consulta que la lección), el enlace a la lección completa y, si la pantalla tiene más, los demás debajo.
export function VentanaComoSeHace({ leccion, lecciones = [], condiciones = false, onElegir, onCerrar }) {
  const qc = useQueryClient()
  const enElCurso = leccion.en_el_curso !== false
  const { data: det, isLoading } = useQuery({
    queryKey: ['formacion-leccion', String(leccion.num)],
    queryFn: () => api.get('/formacion/lecciones/' + leccion.num).then(r => r.data),
    enabled: enElCurso && !condiciones, retry: false, staleTime: 60 * 1000,
  })
  const cerrarRef = useRef(null)
  useEffect(() => {
    try { if (cerrarRef.current && cerrarRef.current.focus) cerrarRef.current.focus() } catch (_) { /* sin foco */ }
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return undefined
    const tecla = (e) => { if (e && e.key === 'Escape') onCerrar() }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [leccion.num])   // eslint-disable-line react-hooks/exhaustive-deps
  const alVer = () => {
    qc.invalidateQueries({ queryKey: ['formacion-catalogo'] })
    qc.invalidateQueries({ queryKey: ['formacion-resumen'] })
    qc.invalidateQueries({ queryKey: ['formacion-leccion', String(leccion.num)] })
  }
  const listo = !enElCurso || condiciones || !isLoading
  const capitulos = (det && det.leccion && Array.isArray(det.leccion.capitulos)) ? det.leccion.capitulos : []
  const otras = lecciones.filter(l => l.num !== leccion.num)
  const idTitulo = 'como-se-hace-' + leccion.num
  const ventana = (
    <div className="modal-safe" data-ventana-como-se-hace={leccion.num} onClick={(e) => { if (e.target === e.currentTarget) onCerrar() }}
      style={{ position: 'fixed', inset: 0, zIndex: 'var(--capa-ventana)', background: 'rgba(8,12,20,.62)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px', overflowY: 'auto' }}>
      <div role="dialog" aria-modal="true" aria-labelledby={idTitulo}
        style={{ width: '100%', maxWidth: '880px', background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--border)',
          borderRadius: '16px', boxShadow: '0 24px 64px rgba(0,0,0,.35)', padding: 'clamp(12px, (2.4vw / var(--z, 1)), 22px)', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '11.5px', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--primary-tinta)' }}>
              {tDato(TXT_BOTON)}
            </div>
            <h2 id={idTitulo} style={{ margin: '3px 0 0', fontSize: 'clamp(17px, (2.2vw / var(--z, 1)), 21px)', fontWeight: 800, lineHeight: 1.25 }}>{leccion.titulo}</h2>
          </div>
          <button ref={cerrarRef} type="button" onClick={onCerrar} aria-label={t('components.ComoSeHace.cerrar', 'Cerrar')} title={t('components.ComoSeHace.cerrar', 'Cerrar')}
            style={{ width: '40px', height: '40px', flexShrink: 0, borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-secondary)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
            <Ico n="close" s={18} />
          </button>
        </div>
        {condiciones ? (
          <div role="status" data-como-se-hace-condiciones="" style={{ borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--surface-2)', padding: '18px 20px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700 }}>{t('components.ComoSeHace.condiciones_titulo', 'Antes del primer vídeo, acepta las condiciones de la Formación')}</div>
            <div style={{ marginTop: '6px', fontSize: '13.5px', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
              {t('components.ComoSeHace.condiciones_texto', 'Se hace una sola vez. Después, los vídeos se abren aquí mismo, en cada pantalla.')}
            </div>
            <Link to="/formacion" onClick={onCerrar} style={{ display: 'inline-block', marginTop: '12px', color: 'var(--primary-tinta)', fontWeight: 700, textDecoration: 'underline' }}>
              {t('components.ComoSeHace.abrir_formacion', 'Abrir la Formación')}
            </Link>
          </div>
        ) : listo ? (
          <ReproductorFormacion key={leccion.num} num={leccion.num} duracionSeg={leccion.duracion_seg} capitulos={capitulos}
            posicionInicial={(det && Number(det.posicion_seg)) || 0} onVista={alVer} />
        ) : (
          <div aria-hidden="true" style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: '10px', background: '#000' }} />
        )}
        {enElCurso && (
          <Link to={'/formacion/' + leccion.num} onClick={onCerrar} data-como-se-hace-leccion-entera=""
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '12px', color: 'var(--primary-tinta)', fontSize: '13.5px', fontWeight: 700, textDecoration: 'none' }}>
            <Ico n="birrete" s={16} /> {t('components.ComoSeHace.leccion_entera', 'Ver la lección completa en la Formación')}
          </Link>
        )}
        {otras.length > 0 && (
          <div style={{ marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
            <div style={{ fontSize: '11.5px', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '8px' }}>
              {t('components.ComoSeHace.mas_videos', 'Más vídeos de esta pantalla')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {otras.map(l => (
                <button key={l.num} type="button" data-otro-como-se-hace={l.num} onClick={() => onElegir(l.num)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', maxWidth: '100%', padding: '7px 12px 7px 8px', borderRadius: '999px', border: '1px solid var(--border)',
                    background: 'var(--surface)', color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textAlign: 'left', lineHeight: 1.3 }}>
                  <span aria-hidden="true" style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--primary-soft)', color: 'var(--primary-tinta)', paddingLeft: '2px', boxSizing: 'border-box' }}>
                    <Ico n="play" s={9} fill="currentColor" sw={0} />
                  </span>
                  <span style={{ minWidth: 0 }}>{l.titulo}</span>
                  {minutos(l.duracion_seg) && <span style={{ flexShrink: 0, color: 'var(--text-muted)', fontWeight: 500 }}>{minutos(l.duracion_seg)}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
  return typeof document !== 'undefined' && document.body ? createPortal(ventana, document.body) : ventana
}
